import { FC, useState } from 'react';
import { DomainStatus, Project, SubscriptionState } from 'store/api/apiTypes';
import cls from './domain-row.module.scss';
import Button from 'shared/ui/button/button';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { getTrialDaysLeft } from 'shared/helpers/getTrialDaysLeft';
import { formatDate } from 'shared/helpers/format-date';
import { useTranslation } from 'hooks/api/useTranslation';
import { useNavigate } from 'react-router-dom';
import {
  useAssignDomainMutation,
  useRemoveDomainMutation,
  useUnassignDomainMutation,
} from 'store/api/userApiSlice';
import { snackbar } from 'shared/helpers/snackbar';
import { RemoveWebsiteModal } from './remove-website-modal';

interface DomainRowProps {
  domain: Project;
  assignAvailable: boolean;
  onBuyLicenses: () => void;
}

interface MenuAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

const UNASSIGN_LOCK_DAYS = 30;

const getUnassignAvailable = (
  domain: Project
): { available: boolean; daysLeft: number } => {
  if (!domain.data?.assign_date) {
    return { available: false, daysLeft: 0 };
  }
  const assignDate = new Date(domain.data.assign_date);
  if (isNaN(assignDate.getTime())) {
    return { available: false, daysLeft: 0 };
  }
  const diffInDays = (Date.now() - assignDate.getTime()) / 86400000;
  return {
    available: diffInDays > UNASSIGN_LOCK_DAYS,
    daysLeft: Math.max(0, Math.ceil(UNASSIGN_LOCK_DAYS - diffInDays)),
  };
};

const getDaysUntil = (dateStr: string): number | null => {
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;

  const targetMidnight = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  ).getTime();
  const today = new Date();
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).getTime();

  return Math.max(0, Math.round((targetMidnight - todayMidnight) / 86400000));
};

export const DomainRow: FC<DomainRowProps> = ({
  domain,
  assignAvailable,
  onBuyLicenses,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [assignDomain] = useAssignDomainMutation();
  const [unassignDomain] = useUnassignDomainMutation();
  const [removeDomain] = useRemoveDomainMutation();

  if (domain.data?.domain_state === undefined) {
    return null;
  }

  const domainState = domain.data.domain_state;
  const daysLeft = getTrialDaysLeft(domain.reports?.[0]) ?? 0;
  const trialExpired = domainState === DomainStatus.TRIAL && daysLeft === 0;
  const isCancelling =
    domain.subscription?.state === SubscriptionState.CANCELING;

  const getStatus = (): { label: string; meta: string | null } => {
    if (domainState === DomainStatus.TERMINATED) {
      return { label: t('terminated'), meta: t('reportsUnavailable') };
    }
    if (domainState === DomainStatus.NO_LICENSE) {
      return { label: t('noSubscription'), meta: t('assignToStartAnalysis') };
    }
    if (domainState === DomainStatus.EXPIRED) {
      return { label: t('expired'), meta: t('reportsUnavailable') };
    }
    if (domainState === DomainStatus.UNASSIGNED) {
      return { label: t('unassigned'), meta: t('viewOnly') };
    }
    if (domainState === DomainStatus.TRIAL) {
      return trialExpired
        ? { label: t('trialExpired'), meta: t('viewOnly') }
        : { label: t('trial'), meta: `${daysLeft} ${t('daysLeft')}` };
    }
    if (isCancelling) {
      const activeUntil = domain.subscription?.active_until
        ? formatDate(new Date(domain.subscription.active_until), 'long')
        : '';
      return {
        label: t('cancelling'),
        meta: activeUntil ? `${t('activeUntil')} ${activeUntil}` : null,
      };
    }
    const billingDaysLeft = domain.subscription?.next_billing_date
      ? getDaysUntil(domain.subscription.next_billing_date)
      : null;
    return {
      label: t('active'),
      meta:
        billingDaysLeft !== null ? `${billingDaysLeft} ${t('daysLeft')}` : null,
    };
  };

  const status = getStatus();

  const handleView = () => {
    localStorage.setItem('selectedDomain', domain.domain);
    navigate('/dashboard');
  };

  const handleActivate = async () => {
    if (!assignAvailable) {
      onBuyLicenses();
      return;
    }
    try {
      await assignDomain({ domain: domain.domain }).unwrap();
      snackbar(t('domainActivatedSuccessfully'));
    } catch (err) {
      snackbar(t('failedToActivateDomain'));
    }
  };

  const handleUnassign = async () => {
    try {
      await unassignDomain({ domain: domain.domain }).unwrap();
      snackbar(t('domainUnassignedSuccessfully'));
    } catch (err) {
      snackbar(t('failedToUnassignDomain'));
    }
  };

  const handleRemove = async () => {
    try {
      await removeDomain(domain.data?.id ?? domain.domain).unwrap();
      setIsRemoveModalOpen(false);
      snackbar(t('domainRemovedSuccessfully'));
    } catch (err) {
      snackbar(t('failedToRemoveDomain'));
    }
  };

  const showViewButton =
    domainState === DomainStatus.PAID ||
    domainState === DomainStatus.TERMINATED;
  const showMenu = domainState !== DomainStatus.TERMINATED;

  const menuItems: MenuAction[] = [];
  if (domainState !== DomainStatus.NO_LICENSE) {
    menuItems.push({ label: t('viewDashboard'), onClick: handleView });
  }
  if (domainState === DomainStatus.PAID && !isCancelling) {
    const unassignAvailable = getUnassignAvailable(domain);
    menuItems.push({
      label: unassignAvailable.available
        ? t('unassignLicense')
        : t('unassignLicenseAvailableIn', '_global', '', {
            days: unassignAvailable.daysLeft,
          }),
      onClick: handleUnassign,
      disabled: !unassignAvailable.available,
    });
  }
  if (
    domainState === DomainStatus.UNASSIGNED ||
    domainState === DomainStatus.NO_LICENSE
  ) {
    menuItems.push({
      label: t('removeFromXoviAi'),
      onClick: () => setIsRemoveModalOpen(true),
    });
  }

  return (
    <>
      <tr className={cls.row}>
        <td className={`${cls.firstCell} ${cls.cell}`}>{domain.domain}</td>
        <td className={cls.cell}>
          <span className={cls.statusLabel}>{status.label}</span>
          {status.meta !== null && (
            <>
              {' '}
              • <span>{status.meta}</span>
            </>
          )}
        </td>
        <td align='right' className={`${cls.cell} ${cls.actionsCell}`}>
          {showViewButton ? (
            <Button theme='tonal' tone='default' onClick={handleView}>
              {t('view')}
            </Button>
          ) : (
            <Button theme='filled' tone='default' onClick={handleActivate}>
              {t('assign')}
            </Button>
          )}
          {showMenu ? (
            <Menu>
              <MenuButton
                className={cls.menuButton}
                data-testid='domain-row-menu-button'
              >
                <Icon pathToIcon={ICONS.verticalDots} size={24} />
              </MenuButton>
              <MenuItems
                anchor='top end'
                modal={false}
                className={cls.menuItems}
              >
                {menuItems.map(item => (
                  <MenuItem
                    key={item.label}
                    as='button'
                    className={
                      cls.menuItem + (item.disabled ? ` ${cls.disabled}` : '')
                    }
                    onClick={item.onClick}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
          ) : (
            <span className={cls.menuButtonPlaceholder} aria-hidden='true' />
          )}
        </td>
      </tr>
      {isRemoveModalOpen && (
        <RemoveWebsiteModal
          isOpen={isRemoveModalOpen}
          domain={domain.domain}
          onClose={() => setIsRemoveModalOpen(false)}
          onConfirm={handleRemove}
        />
      )}
    </>
  );
};
